/**
 * ElegirPagoDialog — qué pago se va a corregir.
 *
 * Las demás correcciones operan sobre la reserva y les basta su código. La
 * cuenta de destino opera sobre UN pago, y una reserva puede tener varios: el
 * cobro del muelle, un abono de la agencia, la comisión anticipada, un cruce.
 * Por eso en la ficha esta corrección cuelga del lápiz de cada línea, donde
 * ya se sabe a qué pago se apunta. Acá se elige primero.
 *
 * Se filtra con las mismas reglas del lápiz, para que las dos puertas lleven
 * al mismo sitio:
 *   · los asientos contables no tienen cuenta que corregir (comisión
 *     anticipada, cruces, descuentos): no se ofrecen
 *   · pasados tres días del pasadía la plata ya se concilió: no se ofrece
 */
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, Chip, Alert,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatPrecio } from './shared/format';

// Mismos tres días que el lápiz de la ficha, y por la misma razón.
export const DIAS_PARA_CORREGIR = 3;

/** Los asientos contables no tienen cuenta de destino que corregir. */
const SUBTIPOS_SIN_CUENTA = ['comision_anticipada', 'cruce',
                             'descuento_comision', 'descuento_comercial'];

/**
 * ¿Se le puede corregir la cuenta a este movimiento?
 *
 * `permiteVouchers` existe porque los dos caminos tienen dueños distintos. El
 * pago va por `/correcciones/pagos/{id}/cuenta-destino`, que admite a todo el
 * backoffice; el voucher va por `/vouchers/{id}/editar`, que exige admin. Y no
 * se pierde nada al ocultar el voucher: al corregir el pago, el servicio
 * sincroniza por su cuenta los vouchers del mismo monto.
 */
export function movimientoCorregible(m, permiteVouchers = true) {
  if (!m) return false;
  if (m.tipo === 'voucher') return permiteVouchers;
  return m.tipo === 'pago_contable'
    && !SUBTIPOS_SIN_CUENTA.includes(m.subtipo)
    && m.metadata?.metodo_pago !== 'cruce_comision';
}

/** Hasta cuándo se puede corregir, a partir del día del pasadía. */
export function venceCorreccion(diaSalida) {
  if (!diaSalida) return null;
  const [a, m, d] = String(diaSalida).slice(0, 10).split('-').map(Number);
  if (!a || !m || !d) return null;
  return new Date(a, m - 1, d + DIAS_PARA_CORREGIR);
}

export function dentroDelPlazo(diaSalida) {
  const vence = venceCorreccion(diaSalida);
  if (!vence) return true;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  return hoy <= vence;
}

const fmtDia = (f) => f?.toLocaleDateString('es-CO',
  { day: '2-digit', month: 'short', year: 'numeric' });

export default function ElegirPagoDialog({
  open, onClose, reserva, movimientos = [], onElegir, permiteVouchers = true,
}) {
  const diaSalida = String(reserva?.salida?.fecha || reserva?.salida_fecha || '').slice(0, 10);
  const vence = venceCorreccion(diaSalida);
  const enPlazo = dentroDelPlazo(diaSalida);
  const candidatos = movimientos.filter(m => movimientoCorregible(m, permiteVouchers));

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Corregir la cuenta de un pago
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · elige cuál pago
        </Typography>
      </DialogTitle>

      <DialogContent>
        {!enPlazo && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
            El plazo venció el {fmtDia(vence)}: son {DIAS_PARA_CORREGIR} días después del
            pasadía. Pasado eso la plata ya se concilió contra el extracto, y moverla de
            cuenta tiene que pasar por una nota contable.
          </Alert>
        )}

        {candidatos.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#64748b', py: 2, textAlign: 'center' }}>
            Esta reserva no tiene pagos con cuenta de destino que corregir.
            {movimientos.length > 0
              ? ' Los asientos contables —comisión anticipada, cruces, descuentos— no llevan cuenta.'
              : ''}
          </Typography>
        ) : (
          <Box>
            {candidatos.map((m, i) => (
              <Box key={`${m.tipo}-${m.metadata?.pago_id || m.metadata?.voucher_id || i}`}
                   onClick={() => enPlazo && onElegir?.(m)}
                   sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1,
                         borderTop: i ? '1px solid #f1f5f9' : 'none',
                         cursor: enPlazo ? 'pointer' : 'not-allowed',
                         opacity: enPlazo ? 1 : 0.55,
                         '&:hover': enPlazo ? { bgcolor: '#f8fafc' } : {} }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>
                      {formatPrecio(m.monto, reserva?.moneda)}
                    </Typography>
                    <Chip size="small" label={m.tipo === 'voucher' ? 'voucher' : m.subtipo}
                          sx={{ height: 17, fontSize: 9.5, fontWeight: 700,
                                bgcolor: '#f1f5f9', color: '#475569' }} />
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                    {m.descripcion || m.origen_label || '—'}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                    {/* La cuenta viaja dentro de `metadata`, no al nivel de arriba. */}
                    {m.metadata?.cuenta_destino_nombre
                      ? `Cuenta actual: ${m.metadata.cuenta_destino_nombre}`
                      : 'Sin cuenta asignada'}
                    {m.metadata?.metodo_pago ? ` · ${m.metadata.metodo_pago}` : ''}
                    {m.referencia ? ` · ref ${m.referencia}` : ''}
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 18, color: '#cbd5e1', flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
